import type { ParallelUploadInitResponse } from "@xiaotian-vault/shared";

type ParallelUploadOptions = {
  csrfToken: string | null;
  chunkSize: number;
  parallelism: number;
  onProgress: (bytesUploaded: number, bytesTotal: number) => void;
  onSuccess: () => void | Promise<void>;
  onError: (error: Error) => void;
};

export type UploadController = {
  start: () => void;
  abort: () => void;
};

export function createParallelUpload(file: File, options: ParallelUploadOptions): UploadController {
  const chunkCount = Math.ceil(file.size / options.chunkSize);
  const completed = new Array<boolean>(chunkCount).fill(false);
  const progressByChunk = new Array<number>(chunkCount).fill(0);
  const active = new Map<number, XMLHttpRequest>();
  let uploadId: string | null = null;
  let initPromise: Promise<ParallelUploadInitResponse> | null = null;
  let paused = true;
  let failed = false;
  let completing = false;

  function start() {
    if (failed || completing) return;
    paused = false;
    initPromise ??= initUpload(file, options);
    initPromise.then((init) => {
      uploadId = init.uploadId;
      pump();
    }).catch(fail);
  }

  function abort() {
    paused = true;
    for (const [index, request] of active) {
      progressByChunk[index] = completed[index] ? chunkSizeAt(index) : 0;
      request.abort();
    }
    active.clear();
    emitProgress();
  }

  function pump() {
    if (paused || failed || completing || !uploadId) return;

    while (active.size < options.parallelism) {
      const index = nextChunkIndex();
      if (index === null) break;
      uploadChunk(index);
    }

    if (!completed.every(Boolean) || active.size > 0) return;
    completing = true;
    completeUpload(uploadId, options)
      .then(options.onSuccess)
      .catch(fail);
  }

  function nextChunkIndex(): number | null {
    for (let index = 0; index < chunkCount; index += 1) {
      if (!completed[index] && !active.has(index)) return index;
    }
    return null;
  }

  function uploadChunk(index: number) {
    if (!uploadId) return;
    const request = new XMLHttpRequest();
    const startByte = index * options.chunkSize;
    const endByte = Math.min(file.size, startByte + options.chunkSize);
    const chunk = file.slice(startByte, endByte);
    progressByChunk[index] = 0;
    active.set(index, request);

    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        progressByChunk[index] = Math.min(event.loaded, chunk.size);
        emitProgress();
      }
    };
    request.onload = () => {
      active.delete(index);
      if (request.status >= 200 && request.status < 300) {
        completed[index] = true;
        progressByChunk[index] = chunk.size;
        emitProgress();
        pump();
        return;
      }
      fail(new Error(responseErrorMessage(request)));
    };
    request.onerror = () => {
      active.delete(index);
      fail(new Error("Upload failed while sending a chunk."));
    };
    request.onabort = () => {
      active.delete(index);
    };

    request.open("PUT", `/api/uploads/parallel/${encodeURIComponent(uploadId)}/chunks/${index}`);
    request.withCredentials = true;
    request.setRequestHeader("Content-Type", "application/octet-stream");
    setCsrfHeader(request, options.csrfToken);
    request.send(chunk);
  }

  function chunkSizeAt(index: number): number {
    const startByte = index * options.chunkSize;
    return Math.min(options.chunkSize, file.size - startByte);
  }

  function emitProgress() {
    options.onProgress(
      progressByChunk.reduce((sum, value) => sum + value, 0),
      file.size
    );
  }

  function fail(error: unknown) {
    if (paused || failed) return;
    failed = true;
    for (const request of active.values()) {
      request.abort();
    }
    active.clear();
    options.onError(error instanceof Error ? error : new Error("Upload failed."));
  }

  return { start, abort };
}

async function initUpload(file: File, options: ParallelUploadOptions): Promise<ParallelUploadInitResponse> {
  const response = await fetch("/api/uploads/parallel/init", {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(options.csrfToken),
    body: JSON.stringify({
      fileName: file.name,
      mimeType: file.type || "application/octet-stream",
      sizeBytes: file.size,
      chunkSize: options.chunkSize,
      chunkCount: Math.ceil(file.size / options.chunkSize)
    })
  });

  return readJsonResponse<ParallelUploadInitResponse>(response);
}

async function completeUpload(uploadId: string, options: ParallelUploadOptions): Promise<void> {
  const response = await fetch(`/api/uploads/parallel/${encodeURIComponent(uploadId)}/complete`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(options.csrfToken),
    body: JSON.stringify({})
  });

  await readJsonResponse(response);
}

function jsonHeaders(csrfToken: string | null): Headers {
  const headers = new Headers({ "Content-Type": "application/json" });
  if (csrfToken) headers.set("x-csrf-token", csrfToken);
  return headers;
}

function setCsrfHeader(request: XMLHttpRequest, csrfToken: string | null): void {
  if (csrfToken) request.setRequestHeader("x-csrf-token", csrfToken);
}

async function readJsonResponse<T = unknown>(response: Response): Promise<T> {
  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new Error(payload?.error?.message ?? "Upload request failed.");
  }
  return payload as T;
}

function responseErrorMessage(request: XMLHttpRequest): string {
  try {
    const payload = request.responseText ? JSON.parse(request.responseText) : null;
    return payload?.error?.message ?? `Upload chunk failed with status ${request.status}.`;
  } catch {
    return `Upload chunk failed with status ${request.status}.`;
  }
}
